import { Injectable, OnDestroy } from '@angular/core';

export interface CropDimensions {
   x: number;
   y: number;
   width: number;
   height: number;
}

export interface ImageFilters {
   grayscale?: number;
   contrast?: number;
   brightness?: number;
   sharpen?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ImageProcessorService implements OnDestroy {
   private createdCanvases: HTMLCanvasElement[] = [];

   ngOnDestroy() {
      this.cleanupCreatedCanvases();
   }

   createCanvas(width: number, height: number): HTMLCanvasElement {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      this.createdCanvases.push(canvas);
      return canvas;
   }

   getCanvasContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get 2D context');
      return ctx;
   }

   cleanupCreatedCanvases(): void {
      this.createdCanvases.forEach(c => {
         if (document.body.contains(c)) {
            c.remove();
         }
      });
      this.createdCanvases = [];
   }

   cropImage(
      source: HTMLCanvasElement,
      dimensions: CropDimensions
   ): HTMLCanvasElement {
      const canvas = this.createCanvas(dimensions.width, dimensions.height);
      const ctx = this.getCanvasContext(canvas);

      ctx.drawImage(
         source,
         dimensions.x, dimensions.y,
         dimensions.width, dimensions.height,
         0, 0,
         dimensions.width, dimensions.height
      );

      return canvas;
   }

   applyFilters(
      canvas: HTMLCanvasElement,
      filters: ImageFilters
   ): void {
      const ctx = this.getCanvasContext(canvas);
      const filterString = [
         filters.grayscale ? `grayscale(${filters.grayscale}%)` : '',
         filters.contrast ? `contrast(${filters.contrast}%)` : '',
         filters.brightness ? `brightness(${filters.brightness}%)` : '',
         filters.sharpen ? 'contrast(1.4)' : ''
      ].filter(Boolean).join(' ');

      if (filterString) {
         ctx.filter = filterString;
         ctx.drawImage(canvas, 0, 0);
      }
   }

   canvasToDataURL(canvas: HTMLCanvasElement): string {
      return canvas.toDataURL('image/jpeg', 1.0);
   }
}